import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface FavButtonWithCountProps {
  domain: string;
  favoritesCount: number;
  isFavorited: boolean;
  onToggle: () => void;
  className?: string;
}

export default function FavButtonWithCount({
  domain,
  favoritesCount,
  isFavorited,
  onToggle,
  className,
}: FavButtonWithCountProps) {
  const reduceMotion = useReducedMotion();
  const label = isFavorited ? 'Saved to Favorites' : 'Save to Favorites';
  const countText = `· ${favoritesCount} watching`;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      className={cn(
        'group inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white pr-4 pl-2 py-2',
        'shadow-sm transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0000FF]',
        'min-h-[44px]',
        !reduceMotion && 'hover:shadow-md hover:border-[#0000FF]',
        className,
      )}
      aria-pressed={isFavorited}
      aria-label={
        isFavorited
          ? 'This domain is saved to your favorites'
          : 'Save this domain to your favorites'
      }
    >
      <motion.span
        className="flex items-center justify-center h-11 w-11 rounded-full text-white font-bold"
        style={{
          background: 'radial-gradient(circle at 50% 50%, #000000 0%, #000000 35%, #0000FF 100%)',
          boxShadow: '0 10px 25px rgba(0,0,0,0.18)',
        }}
        whileHover={reduceMotion ? undefined : { scale: 1.03 }}
        whileTap={reduceMotion ? undefined : { scale: 0.97 }}
        aria-hidden
      >
        <span className="text-xl leading-none">+</span>
      </motion.span>

      <div className="flex flex-col text-left leading-tight">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{countText}</span>
      </div>
    </motion.button>
  );
}

